import {
	OrderPaymentStatusNameEnum,
	OrderTypeCodeEnum,
	OrderTypeEnum,
	PaymentMethodEnum,
} from '@core/enums';
import { OrderForm, OrderType, PaymentStatus } from '@core/models';
import {
	CheckoutOrderCreationConfiguration,
	CheckoutOrderCreationContext,
	CheckoutOrderPayloadRule,
} from '../interfaces/checkout-order-creation.interface';
import { transformToUpsert } from './order-payload.domain';
import {
	CheckoutPaymentKind,
	CheckoutPaymentStatusFilter,
	CheckoutPaymentWayFilter,
} from '../enum/checkout-order-creation.enum';

export type CheckoutOrderCreationRule = (
	context: CheckoutOrderCreationContext
) => CheckoutOrderCreationContext;

export const checkoutOrderCreationConfigurationRule: CheckoutOrderCreationRule = context => {
	const paymentKind =
		context.paymentMethod === PaymentMethodEnum.CASH || context.paymentMethod === PaymentMethodEnum.IN_STORE
			? CheckoutPaymentKind.Cash
			: CheckoutPaymentKind.Card;
	const isCash = paymentKind === CheckoutPaymentKind.Cash;

	return {
		...context,
		paymentKind,
		orderStatusFilter: isCash ? OrderPaymentStatusNameEnum.Open : OrderPaymentStatusNameEnum.Closed,
		paymentWayFilter: isCash ? CheckoutPaymentWayFilter.Cash : CheckoutPaymentWayFilter.Credit,
		paymentStatusFilter: isCash ? null : CheckoutPaymentStatusFilter.Completed,
		orderTypeCode:
			context.orderTypeName === OrderTypeEnum.Pickup ? OrderTypeCodeEnum.Pickup : OrderTypeCodeEnum.Delivery,
	};
};

export function filterCheckoutCatalogResponse<T extends { nombre: string }>(
	data: T[],
	filterName: string
): T[] {
	const filter = filterName.toLowerCase();
	return data.filter(item => item.nombre.toLowerCase().includes(filter));
}

export function filterCheckoutOrderTypes(
	orderTypes: OrderType[],
	orderTypeCode: OrderTypeCodeEnum
): OrderType[] {
	return orderTypes.filter(orderType => orderType.codigo === orderTypeCode);
}

export const checkoutOrderPayloadRule: CheckoutOrderPayloadRule = input => {
	const statusRaw = input.orderStatus[0];
	const paymentWayRaw = input.paymentWay[0];
	const paymentStatusRaw = input.paymentStatus[0];
	const typeOrderRaw = input.orderType[0];
	const statusOrder = statusRaw
		? {
				id: statusRaw.id || statusRaw.idEstado || '',
				nombre: statusRaw.nombre,
			}
		: undefined;
	const orderForm: OrderForm = {
		...input.orderForm,
		...(typeOrderRaw && { typeOrder: typeOrderRaw }),
		...(statusOrder && { statusOrder }),
	};
	const paymentWay = paymentWayRaw
		? {
				idFormaPago: paymentWayRaw.idFormaPago || paymentWayRaw.id || '',
			}
		: null;
	const paymentStatus: PaymentStatus | null = paymentStatusRaw
		? {
				...paymentStatusRaw,
				idEstado: paymentStatusRaw.idEstado || paymentStatusRaw.id,
			}
		: null;

	return transformToUpsert(
		{
			...orderForm,
			clienteId: input.clientId,
		},
		paymentStatus,
		paymentWay
	);
};

export function createCheckoutOrderCreationConfiguration(
	rules: CheckoutOrderCreationRule[],
	context: Pick<CheckoutOrderCreationContext, 'paymentMethod' | 'orderTypeName'>
): CheckoutOrderCreationConfiguration {
	const initialContext: CheckoutOrderCreationContext = {
		...context,
		paymentKind: null,
		orderStatusFilter: null,
		paymentWayFilter: null,
		paymentStatusFilter: null,
		orderTypeCode: OrderTypeCodeEnum.Delivery,
	};
	const result = rules.reduce((current, rule) => rule(current), initialContext);
	if (
		!result.paymentKind ||
		!result.orderStatusFilter ||
		!result.paymentWayFilter ||
		!result.orderTypeCode
	) {
		throw new Error('Checkout order creation rules did not produce a complete configuration');
	}

	return {
		orderStatusFilter: result.orderStatusFilter,
		paymentWayFilter: result.paymentWayFilter,
		paymentStatusFilter: result.paymentStatusFilter,
		orderTypeCode: result.orderTypeCode,
	};
}
